// Story 1.17's bank-account panel, MOUNTED BY STORY 8.5 ON THE CREATOR PAYOUT
// SURFACE.
//
// It survived Story 8.3's cut rather than being deleted and re-invented,
// because the mod-97 IBAN validation in `./iban.ts` and the
// `wallet_details_required` 412 handoff below are reviewed behaviour with tests
// attached, and re-deriving them under delivery pressure is how a regression
// gets in. Story 8.3's header named three things Story 8.5 had to change before
// mounting it; all three are done:
//
//   1. It is on a CREATOR surface (`<CreatorPayoutPanel>` inside the creator
//      dashboard), never on /account/wallet. A user has nothing to register a
//      bank account FOR: their Greenhold balance is closed-loop and non-payable.
//   2. It points at the EARNINGS pot's rail —
//      GET|POST /api/v1/creators/me/payout/account, approved creators only.
//   3. Every string is re-read for a SUPPLIER being paid rather than a user
//      cashing out. This is where we settle revenue-share invoices to; it is
//      not a withdrawal destination, and no copy here calls it one.
//
// Three states:
//   1. Nothing registered — IBAN + holder-name form with client-side
//      mod-97 checksum validation.
//   2. pending_verification — the provider is checking the IBAN. Poll
//      the payout-account query every 5s for up to ~60s waiting for the
//      "verified" flip; past that, tell the creator to check back.
//   3. verified — show "•••• 1234 (Holder Name)".

import { useEffect, useState } from "react";
import { Button, Input, Label } from "@greenhold/components";
import toast from "react-hot-toast";

import { isValidIban, normalizeIban } from "./iban";
import { usePaymentAccountStatus } from "./usePaymentAccountStatus";
import { useRegisterPayoutAccount } from "./useRegisterPayoutAccount";

const POLL_INTERVAL_MS = 5_000;
const POLL_DURATION_MS = 60_000;

type OnboardingPanelProps = {
  /**
   * Story 1.36 D1 — fires when registering the IBAN hits 412
   * `wallet_details_required` (the identity form is the gate for EVERY
   * money-capable action, bank-account registration included). The caller
   * brings the user to the wallet-details form.
   */
  onRequiresWalletDetails?: () => void;
};

export function OnboardingPanel({
  onRequiresWalletDetails,
}: OnboardingPanelProps = {}) {
  const [iban, setIban] = useState("");
  const [holderName, setHolderName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [needsWalletDetails, setNeedsWalletDetails] = useState(false);
  const [waitingForVerification, setWaitingForVerification] = useState(false);
  const [pollExpiresAt, setPollExpiresAt] = useState<number | null>(null);

  const status = usePaymentAccountStatus({
    refetchIntervalMs: waitingForVerification ? POLL_INTERVAL_MS : undefined,
  });
  const register = useRegisterPayoutAccount();

  // Stop polling once verified OR the window elapses.
  useEffect(() => {
    if (!waitingForVerification) return;
    if (status.data?.status === "verified") {
      setWaitingForVerification(false);
      toast.success("Payout details verified.");
      return;
    }
    if (pollExpiresAt && Date.now() > pollExpiresAt) {
      setWaitingForVerification(false);
      toast(
        "Your payout details are still being verified — check back in a bit or contact support if this persists.",
        { duration: 6_000 },
      );
    }
  }, [status.data?.status, waitingForVerification, pollExpiresAt]);

  if (status.isPending) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading your payout details…
      </p>
    );
  }

  const account = status.data ?? null;

  if (account?.status === "verified") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">We settle your revenue share to</p>
        <p className="font-mono text-base">
          •••• {account.iban_last4}{" "}
          <span className="text-muted-foreground">({account.holder_name})</span>
        </p>
      </div>
    );
  }

  if (account?.status === "pending_verification") {
    return (
      <div className="space-y-2" data-testid="payout-account-pending">
        <p className="text-sm font-medium">Payout details pending verification</p>
        <p className="font-mono text-base">
          •••• {account.iban_last4}{" "}
          <span className="text-muted-foreground">({account.holder_name})</span>
        </p>
        <p className="text-sm text-muted-foreground">
          {waitingForVerification
            ? "Waiting for our payment partner to verify these details…"
            : "Our payment partner is verifying these details. This usually takes a moment."}
        </p>
        {!waitingForVerification && (
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              setWaitingForVerification(true);
              setPollExpiresAt(Date.now() + POLL_DURATION_MS);
            }}
          >
            Check status
          </Button>
        )}
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setNeedsWalletDetails(false);
    const trimmedHolder = holderName.trim();
    if (trimmedHolder.length < 2) {
      setFormError("Enter the account holder's full name.");
      return;
    }
    if (!isValidIban(iban)) {
      setFormError("That IBAN doesn't look right — check for typos.");
      return;
    }
    try {
      const result = await register.mutateAsync({
        iban: normalizeIban(iban),
        holder_name: trimmedHolder,
      });
      setIban("");
      setHolderName("");
      if (result.status === "verified") {
        toast.success("Payout details saved.");
      } else {
        // Watch for the provider's verification flip.
        setWaitingForVerification(true);
        setPollExpiresAt(Date.now() + POLL_DURATION_MS);
      }
    } catch (err) {
      // 412 PRECONDITION_FAILED — wallet details not yet completed. The
      // identity form gates bank-account registration too; surface a clear
      // prompt with a direct affordance to it instead of the raw string.
      if (
        err &&
        typeof err === "object" &&
        "code" in err &&
        (err as { code: string }).code === "wallet_details_required"
      ) {
        setNeedsWalletDetails(true);
        onRequiresWalletDetails?.();
        return;
      }
      const message =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : "Could not save your payout details.";
      setFormError(message);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit} noValidate>
      <p className="text-sm">
        Add the SEPA account we should settle your revenue share to. The
        details go straight to our payment partner for verification — we keep
        only the last four digits.
      </p>
      <div className="space-y-1">
        <Label htmlFor="payout-holder-name">Account holder name</Label>
        <Input
          id="payout-holder-name"
          type="text"
          autoComplete="name"
          value={holderName}
          onChange={(e) => {
            setHolderName(e.target.value);
            setFormError(null);
          }}
          placeholder="As it appears on the account"
          disabled={register.isPending}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="payout-iban">IBAN</Label>
        <Input
          id="payout-iban"
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          value={iban}
          onChange={(e) => {
            setIban(e.target.value);
            setFormError(null);
          }}
          placeholder="DK50 0040 0440 1162 43"
          disabled={register.isPending}
        />
      </div>
      {needsWalletDetails && (
        <div
          className="space-y-2 rounded-md border border-brand-gold-500/40 bg-brand-paper-50 p-3"
          role="alert"
          data-testid="onboarding-needs-wallet-details"
        >
          <p className="text-sm">
            Complete your wallet details first — we need them before we can
            register a payout account.
          </p>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => onRequiresWalletDetails?.()}
          >
            Complete wallet details
          </Button>
        </div>
      )}
      {formError && (
        <p className="text-sm text-destructive" role="alert">
          {formError}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        disabled={register.isPending}
      >
        {register.isPending ? "Saving…" : "Save payout details"}
      </Button>
    </form>
  );
}
